import { type FC, useId } from "react";
import {
  PhoneInput as InternationalPhone,
  type PhoneInputProps as InternationalPhoneProps,
  type ParsedCountry,
} from "react-international-phone";
import "react-international-phone/style.css";

import { getFormattedPhoneNumber } from "@ch/helpers/utils/formValidations";
import type { BaseButtonProps } from "../Buttons/BaseButton";
import type { ButtonProps } from "../Buttons/Button";
import { baseVariants } from "../Buttons/baseVariants";
import variants from "./variants";

type PhoneInputProps = InternationalPhoneProps & {
  error?: string;
  onValueChange?: (value: string) => void;
  onCountryChange?: (value: string) => void;
  onCountryIsoChange?: (iso2: string) => void;
  handleSetEmptyError?: (value?: string) => void;
  errorText?: string;
  label?: string;
  shape?: "rounded" | "square" | "pill";
  size?: BaseButtonProps["size"];
  intent?: ButtonProps["intent"] | "underline";
  inputSize?: number; // This is the default size prop for the input
  labelClassName?: string;
};

const PhoneInput: FC<PhoneInputProps> = ({
  error,
  errorText,
  onValueChange,
  onCountryChange,
  onCountryIsoChange,
  handleSetEmptyError,
  label,
  disabled,
  shape = "rounded",
  size = "base",
  intent = "solid",
  className = "",
  labelClassName = "",
  required,
  ...rest
}) => {
  const inputId = useId();

  const shapeClassName = baseVariants.shape[shape];
  const sizeClassName = baseVariants.size[size];
  const inputClassName = variants[intent];
  const errorClassName = error ? "!border-orange" : "";
  const disabledClassName = disabled ? "opacity-50" : "";

  const handleInputChange = (
    phone: string,
    meta: {
      country: ParsedCountry;
      inputValue: string;
    },
  ) => {
    const countryCode = `+${meta.country.dialCode}`;

    const formattedNumber = getFormattedPhoneNumber(phone, countryCode);

    onValueChange?.(formattedNumber);

    onCountryChange?.(countryCode);
    onCountryIsoChange?.(meta.country.iso2.toUpperCase());

    // set error if phone number input is empty
    const isEmpty = phone.replace(countryCode, "").trim().length === 0;

    if (isEmpty) {
      handleSetEmptyError?.(errorText);
    } else {
      handleSetEmptyError?.();
    }
  };

  return (
    <div>
      <div className="flex flex-col items-stretch gap-2">
        {label && (
          <label
            className={`label-text text-sm text-gray-extras-100 ${labelClassName}`}
            htmlFor={inputId}
          >
            {label}
            {required && <span className="text-sm text-red-400"> *</span>}
          </label>
        )}
        <InternationalPhone
          className={`flex items-center gap-2 transition-all ${shapeClassName} ${sizeClassName} ${inputClassName} ${errorClassName} ${disabledClassName} ${className}`}
          countrySelectorStyleProps={{
            buttonClassName:
              "!h-9 !px-0 !border-none !rounded-lg !bg-transparent",
            flagClassName: "!rounded-full",
            dropdownStyleProps: {
              className: "!rounded-lg",
            },
          }}
          defaultCountry="ng"
          disabled={disabled}
          inputClassName="focus:outline-none flex-1 !bg-transparent !border-r-0 !border-y-0 !text-[100%] !text-[inherit] !border-l !border-gray-200 !rounded-none !pr-0 !pl-2 !h-9"
          inputProps={{
            id: inputId,
          }}
          onChange={handleInputChange}
          {...rest}
        />
      </div>
      {error && <span className="text-xs text-orange">{error}</span>}
    </div>
  );
};

export default PhoneInput;
